import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useActionList } from "../hooks/useActions";
import { NewActionDrawer } from "./NewActionDrawer";

const BORDER = "hsl(210,30%,85%)";

type Props = {
  meetingId?: string;
  taskId?: string;
  decisionId?: string;
  milestoneId?: string;
  prefill?: {
    companyId?: string | null;
    productId?: string | null;
    projectId?: string | null;
    meetingId?: string | null;
    meetingTitle?: string | null;
    taskId?: string | null;
    taskTitle?: string | null;
    taskCode?: string | null;
    decisionId?: string | null;
    decisionTitle?: string | null;
    milestoneId?: string | null;
    milestoneTitle?: string | null;
    locked?: {
      meetingId?: boolean;
      taskId?: boolean;
      decisionId?: boolean;
      milestoneId?: boolean;
    };
  };
};

export function ActionsTabContent({
  meetingId,
  taskId,
  decisionId,
  milestoneId,
  prefill,
}: Props) {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { data, isLoading } = useActionList({
    meetingId,
    taskId,
    decisionId,
    milestoneId,
    limit: 100,
  });
  const actions = data?.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {actions.length} {actions.length === 1 ? "action" : "actions"}
        </div>
        <Button size="sm" onClick={() => setDrawerOpen(true)}>
          <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
          New Action
        </Button>
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          Loading…
        </div>
      ) : actions.length === 0 ? (
        <div
          className="rounded-xl border bg-white py-12 text-center"
          style={{ borderColor: BORDER }}
        >
          <Zap
            className="mx-auto h-8 w-8 text-muted-foreground"
            strokeWidth={1.5}
          />
          <div className="mt-2 text-sm text-muted-foreground">
            No actions yet
          </div>
        </div>
      ) : (
        <div
          className="overflow-hidden rounded-xl border bg-white"
          style={{ borderColor: BORDER }}
        >
          <table className="w-full text-[13px]">
            <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-[14px] py-[9px]" style={{ width: 110 }}>
                  #
                </th>
                <th className="px-[14px] py-[9px]">Title</th>
                <th className="px-[14px] py-[9px]" style={{ width: 120 }}>
                  Status
                </th>
                <th className="px-[14px] py-[9px]" style={{ width: 110 }}>
                  Priority
                </th>
                <th className="px-[14px] py-[9px]" style={{ width: 140 }}>
                  Assignee
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {actions.map((a) => (
                <tr
                  key={a.id}
                  className="cursor-pointer hover:bg-[hsl(210,50%,98%)]"
                  onClick={() => navigate(`/actions/${a.id}`)}
                >
                  <td className="px-[14px] py-[11px] font-mono text-[12px] text-muted-foreground">
                    {a.code}
                  </td>
                  <td className="px-[14px] py-[11px] font-medium text-foreground">
                    {a.title}
                  </td>
                  <td className="px-[14px] py-[11px] text-foreground">
                    {a.status}
                  </td>
                  <td className="px-[14px] py-[11px] text-foreground">
                    {a.priority}
                  </td>
                  <td className="px-[14px] py-[11px] text-muted-foreground">
                    {a.assignedToName ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <NewActionDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        prefill={prefill}
      />
    </div>
  );
}
